// YouTube IFrame Player wrapper for background music
// Plays a YouTube video's audio behind the game

export class YouTubePlayer {
    constructor(videoId = '4xDzrJKXOOY') {
        this.videoId = videoId;
        this.player = null;
        this.isReady = false;
        this.isPlaying = false;
        this.pendingPlay = false;
        this._wasPlayingBeforeHidden = false;

        this._loadAPI();
        this._setupVisibilityHandler();
    }

    /**
     * Handle tab visibility changes to pause/resume music
     */
    _setupVisibilityHandler() {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                // Tab is being hidden - remember if we were playing
                this._wasPlayingBeforeHidden = this.isPlaying;
                // Don't pause - let browser handle it naturally
            } else {
                // Tab is visible again - resume if we were playing before
                if (this._wasPlayingBeforeHidden && !this.isPlaying) {
                    this.play();
                }
            }
        });
    }

    _loadAPI() {
        // Check if API is already loaded
        if (window.YT && window.YT.Player) {
            this._createPlayer();
            return;
        }

        // Load the YouTube IFrame API
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);

        // YouTube API calls this global function when ready
        window.onYouTubeIframeAPIReady = () => {
            this._createPlayer();
        };
    }

    _createPlayer() {
        // Create a container for the player (hidden)
        const container = document.createElement('div');
        container.id = 'yt-player-container';
        container.style.cssText = `
            position: fixed;
            bottom: 0;
            left: 0;
            width: 1px;
            height: 1px;
            opacity: 0;
            pointer-events: none;
            z-index: -1;
        `;
        document.body.appendChild(container);

        const playerDiv = document.createElement('div');
        playerDiv.id = 'yt-player';
        container.appendChild(playerDiv);

        this.player = new YT.Player('yt-player', {
            videoId: this.videoId,
            playerVars: {
                autoplay: 0,
                controls: 0,
                disablekb: 1,
                fs: 0,
                loop: 1,
                modestbranding: 1,
                playlist: this.videoId, // Required for loop to work
                playsinline: 1,
                rel: 0
            },
            events: {
                onReady: () => this._onReady(),
                onStateChange: (e) => this._onStateChange(e),
                onError: (e) => this._onError(e)
            }
        });
    }

    _onReady() {
        this.isReady = true;
        this.player.setVolume(50);

        // If play was requested before ready, play now
        if (this.pendingPlay) {
            this.pendingPlay = false;
            this.play();
        }
    }

    _onStateChange(event) {
        // YT.PlayerState: ENDED=0, PLAYING=1, PAUSED=2, BUFFERING=3, CUED=5
        if (event.data === YT.PlayerState.PLAYING) {
            this.isPlaying = true;
        } else if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
            this.isPlaying = false;
        }
    }

    _onError(event) {
        console.warn('[YouTubePlayer] Error:', event.data);
    }

    play() {
        if (!this.isReady) {
            this.pendingPlay = true;
            return;
        }

        try {
            this.player.playVideo();
            this.isPlaying = true;
        } catch (e) {
            // Player not ready yet
        }
    }

    pause() {
        if (!this.isReady) {
            this.pendingPlay = false;
            return;
        }

        try {
            this.player.pauseVideo();
            this.isPlaying = false;
        } catch (e) {
            // Player not ready yet
        }
    }

    toggle() {
        if (this.isPlaying) {
            this.pause();
        } else {
            this.play();
        }
        return this.isPlaying;
    }

    setVolume(volume) {
        if (!this.isReady) return;
        // YouTube volume is 0-100
        this.player.setVolume(Math.max(0, Math.min(100, volume)));
    }

    getVolume() {
        if (!this.isReady) return 50;
        return this.player.getVolume();
    }

    isCurrentlyPlaying() {
        return this.isPlaying;
    }

    /**
     * Fade in volume from 0 to target over duration
     * @param {number} targetVolume - Target volume (0-100)
     * @param {number} duration - Fade duration in milliseconds
     */
    fadeIn(targetVolume = 50, duration = 2000) {
        if (!this.isReady) {
            // Store fade params to apply when ready
            this._pendingFade = { targetVolume, duration };
            return;
        }

        this.player.setVolume(0);
        const steps = 20;
        const stepDuration = duration / steps;
        const volumeStep = targetVolume / steps;
        let currentStep = 0;

        const fadeInterval = setInterval(() => {
            currentStep++;
            const newVolume = Math.min(targetVolume, volumeStep * currentStep);
            this.player.setVolume(newVolume);

            if (currentStep >= steps) {
                clearInterval(fadeInterval);
            }
        }, stepDuration);
    }

    /**
     * Play with fade-in effect
     * @param {number} targetVolume - Target volume (0-100)
     * @param {number} duration - Fade duration in milliseconds
     */
    playWithFadeIn(targetVolume = 50, duration = 2000) {
        this.play();
        this.fadeIn(targetVolume, duration);
    }
}
